import {
  finalizeTreatmentComment,
  isMedicationSwitch,
  type PrescriptionOrder,
  type PublishTreatmentStatusDecision,
  type TreatmentEndReason,
  type TreatmentStatusOutcome,
} from "@care-by-ella/contracts";

// TES-1291 (TES-1267 §4, A-02 §7) — before a pause, a resumption, an end or a
// medication switch is published, the doctor sees what it changes for the
// patient: what Din behandling will show, what happens on Hem, and where the
// decision is recorded. Each line describes what the server does today (see
// TreatmentStatusDecisionService and the readers of `treatment_pauses`);
// none of it is a promise the code does not keep.
export interface StatusOutcomeEffects {
  dinBehandling: string;
  hem: string;
  history: string;
  subscription: string;
}

const HISTORY =
  "Ditt besked publiceras under Din vård, och beslutet loggas med dig som signerande läkare.";

export const STATUS_OUTCOME_EFFECTS: Record<
  TreatmentStatusOutcome,
  StatusOutcomeEffects
> = {
  pause: {
    dinBehandling:
      "Behandlingen och den aktuella receptperioden pausas. Ingen nästa planerad dos visas under uppehållet.",
    hem: "Ingen dosuppgift skapas under uppehållet. Beskedet visas som nytt besked från läkaren.",
    history: HISTORY,
    subscription: "Abonnemanget påverkas inte.",
  },
  resume: {
    dinBehandling:
      "Behandlingen och receptperioden blir aktiva igen. Nästa planerade dos visas först när ett nytt startdatum är bekräftat i dosschemat.",
    hem: "Dosuppgiften kommer tillbaka från det nya startdatumet. Beskedet visas som nytt besked från läkaren.",
    history: HISTORY,
    subscription: "Abonnemanget påverkas inte.",
  },
  end: {
    dinBehandling:
      "Behandlingen avslutas och öppna receptperioder avslutas. Ingen nästa planerad dos visas.",
    hem: "Inga fler dos- eller förnyelseuppgifter skapas. Beskedet visas som nytt besked från läkaren.",
    history: HISTORY,
    subscription: "Abonnemanget sägs inte upp automatiskt.",
  },
  // A switch is written as a dose adjustment is (`adjustDose`): the order on
  // the current period, a CP-22 version of it, a `dose_adjusted` event that
  // Hem shows for five days and Din resa lists as a follow-up.
  switch: {
    dinBehandling:
      "Den aktuella receptperioden får den nya ordinationen. Den officiella instruktionen följer det nya läkemedlet när det är valt ur katalogen.",
    hem: "”Din plan har uppdaterats” visas i fem dagar, med texten ”Din dos har justerats – se din plan”. Beskedet visas som nytt besked från läkaren.",
    history: `${HISTORY} Ordinationen före och efter bytet sparas, och Din resa får posten ”Uppföljning från din läkare”.`,
    subscription: "Abonnemanget påverkas inte.",
  },
};

// TES-1554 (Ö-861–Ö-863) — what an end with the reason not_suitable adds:
// the binding and notice periods go from today, and admin gets a task to end
// the subscription and refund the paid days from today, by hand in Stripe.
export const NOT_SUITABLE_SUBSCRIPTION_EFFECT =
  "Bindnings- och uppsägningstiden gäller inte från i dag. Admin får en uppgift att avsluta abonnemanget och återbetala betald tid från och med i dag.";

/** What the subscription line of the preview says for this decision. */
export function statusSubscriptionEffect(
  outcome: TreatmentStatusOutcome,
  endReason: TreatmentEndReason | null | undefined,
): string {
  if (outcome === "end" && endReason === "not_suitable") {
    return NOT_SUITABLE_SUBSCRIPTION_EFFECT;
  }
  return STATUS_OUTCOME_EFFECTS[outcome].subscription;
}

export interface StatusDecisionDraft {
  outcome: TreatmentStatusOutcome | null;
  patientSummary: string;
  journalDone: boolean;
  // A switch only: the new order once its required parts are filled in and
  // any deviation is motivated (null until then), and the medication she is
  // on now.
  order?: PrescriptionOrder | null;
  currentMedication?: string | null;
  // TES-1554 — an end only: the reasons the server asks for (empty while the
  // tenant flag is off, and then no reason is sent) and the one picked.
  endReasons?: readonly TreatmentEndReason[];
  endReason?: TreatmentEndReason | null;
}

type StatusDecisionField =
  | "outcome"
  | "endReason"
  | "order"
  | "summary"
  | "webdoc";

const asksEndReason = (draft: StatusDecisionDraft) =>
  draft.outcome === "end" && (draft.endReasons?.length ?? 0) > 0;

/** What still stands in the way of publishing, per field, in her words. */
export function statusDecisionErrors(
  draft: StatusDecisionDraft,
): Partial<Record<StatusDecisionField, string>> {
  const e: Partial<Record<StatusDecisionField, string>> = {};
  if (draft.outcome === null) e.outcome = "Välj ett beslut.";
  if (asksEndReason(draft) && !draft.endReason)
    e.endReason = "Välj anledning till avslutet.";
  if (draft.outcome === "switch") {
    if (!draft.order) {
      e.order = "Välj läkemedel, dos och doseringsintervall.";
    } else if (
      !isMedicationSwitch(draft.currentMedication, draft.order.medication)
    ) {
      e.order =
        "Välj ett annat läkemedel än det nuvarande. En ny dos av samma läkemedel beslutas under Månadsincheckning.";
    }
  }
  if (!finalizeTreatmentComment(draft.patientSummary))
    e.summary = "Skriv kommentaren till patienten.";
  if (!draft.journalDone)
    e.webdoc = "Bekräfta att journalanteckningen är införd i Webdoc.";
  return e;
}

/** The request body, or null while anything is missing. */
export function statusDecisionBody(
  draft: StatusDecisionDraft,
): PublishTreatmentStatusDecision | null {
  if (Object.keys(statusDecisionErrors(draft)).length > 0) return null;
  return {
    outcome: draft.outcome!,
    // TES-1290 — the comment as it is published.
    patientSummary: finalizeTreatmentComment(draft.patientSummary),
    webdocControl: { journalInWebdoc: true },
    ...(draft.outcome === "switch" && draft.order
      ? { order: draft.order }
      : {}),
    ...(asksEndReason(draft) && draft.endReason
      ? { endReason: draft.endReason }
      : {}),
  };
}
